import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/common/Breadcrumb'
import ReactDataTable from 'react-data-table-component';
import { IconBan, IconCheck, IconEye, IconX, IconCircleCheck, IconAlertTriangle } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { useManagerPendingTimesheets, useHrPendingTimesheets, useApproveTimesheet, useHrApproveTimesheet, useRejectTimesheet } from '../../hooks/useTimesheets';
import {
    useLaborTimesheetsList,
    useLaborTimesheetApprove,
    useLaborTimesheetHrApprove,
    useLaborTimesheetReject,
} from '../../hooks/useLaborTimesheets';
import { useEmployees } from '../../hooks/useEmployees';
import ActionDropdown from '../../components/common/ActionDropdown';

const AVATAR_COLORS = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777'];

export default function TimesheetApprovals() {
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    const isHR = user?.is_superuser || hasAnyPermission(['rbacapp.hr_act', 'rbacapp.hr_approve', 'rbacapp.hr_view']);

    const { data: employees } = useEmployees(orgId);

    const { data: managerPendingTimesheets, isLoading: isLoadingManager } = useManagerPendingTimesheets(
        !isHR ? orgId : null,
        { suppressToast: true }
    );
    const { data: hrPendingTimesheets, isLoading: isLoadingHR } = useHrPendingTimesheets(isHR ? orgId : null);

    const pendingTimesheets = isHR ? hrPendingTimesheets : managerPendingTimesheets;
    const isLoading = isHR ? isLoadingHR : isLoadingManager;

    const managerApprove = useApproveTimesheet();
    const hrApprove = useHrApproveTimesheet();
    const rejectTimesheet = useRejectTimesheet();

    // Labour-distribution sheets: no dedicated "pending" endpoint — the backend
    // scopes the plain list (manager sees reports', HR sees all), so filter by
    // status client-side. If this ever 403s (e.g. caller isn't shared-services
    // allocated themselves), it just resolves to no data — labour rows are
    // silently omitted rather than breaking the standard approvals list.
    const { data: laborTimesheetsRaw } = useLaborTimesheetsList();
    const laborApprove = useLaborTimesheetApprove();
    const laborHrApprove = useLaborTimesheetHrApprove();
    const laborReject = useLaborTimesheetReject();

    const laborRows = useMemo(() => {
        const list = Array.isArray(laborTimesheetsRaw) ? laborTimesheetsRaw : (laborTimesheetsRaw?.results || []);
        return list
            .filter((s) => (isHR ? ['submitted', 'approved'].includes(s.status) : s.status === 'submitted'))
            .map((s) => ({
                id: s.id,
                kind: 'labor',
                employee: s.employee,
                employee_id: s.employee,
                month: s.month,
                year: s.year,
                total_hours: s.month_totals?.grand_total || 0,
                submitted_at: s.submitted_at,
                manager_approved_at: s.approved_at,
                status: s.status,
            }));
    }, [laborTimesheetsRaw, isHR]);

    const timesheets = [
        ...(pendingTimesheets?.timesheets || pendingTimesheets?.results || (Array.isArray(pendingTimesheets) ? pendingTimesheets : []))
            .map((t) => ({ ...t, kind: 'standard' })),
        ...laborRows,
    ];

    const [approveModal, setApproveModal] = useState({ isOpen: false, timesheet: null });
    const [rejectModal, setRejectModal] = useState({ isOpen: false, timesheet: null });
    const [rejectReason, setRejectReason] = useState('');

    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle] = useState(false);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const getMonthName = (month) => {
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        return months[month - 1] || '';
    };

    const getStatusBadge = (status) => {
        const statusLower = status?.toLowerCase();
        const map = {
            signed: <span className='badge badge-warning'>Pending Approval</span>,
            submitted: <span className='badge badge-warning'>Pending Approval</span>,
            manager_approved: <span className='badge badge-info'>Pending HR Review</span>,
            approved: <span className='badge badge-success'>Approved</span>,
            rejected: <span className='badge badge-danger'>Rejected</span>,
        };
        return map[statusLower] || <span className='badge badge-secondary'>{status}</span>;
    };

    // Labour statuses ("submitted" / "approved" / "hr_approved") use a different
    // taxonomy than standard ("signed" / "submitted" / "manager_approved" / "approved").
    // Translate before badging so "approved" (line-manager-only, mid-flow for labour)
    // doesn't render as the standard flow's terminal "Approved" state.
    const displayStatus = (row) => (row.kind === 'labor' && row.status === 'approved' ? 'manager_approved' : row.status);

    // ACTIONS ---
    const confirmApprove = () => {
        const row = approveModal.timesheet;
        if (row.kind === 'labor') {
            const hook = isHR ? laborHrApprove : laborApprove;
            hook.mutate({ id: row.id }, {
                onSuccess: () => {
                    toast.success(isHR ? 'Timesheet approved by HR successfully' : 'Timesheet approved successfully');
                    setApproveModal({ isOpen: false, timesheet: null });
                },
                onError: (err) => toast.error(err?.response?.data?.detail || `Error: ${err.message}`)
            });
            return;
        }
        const approveHook = isHR ? hrApprove : managerApprove;
        approveHook.mutate({ id: row.id, orgId }, {
            onSuccess: () => {
                toast.success(isHR ? 'Timesheet approved by HR successfully' : 'Timesheet approved successfully');
                setApproveModal({ isOpen: false, timesheet: null });
            },
            onError: (err) => toast.error(`Error: ${err.message}`)
        });
    };

    const confirmReject = () => {
        if (!rejectReason.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }
        const row = rejectModal.timesheet;
        if (row.kind === 'labor') {
            laborReject.mutate({ id: row.id, reason: rejectReason }, {
                onSuccess: () => {
                    toast.success('Timesheet rejected successfully');
                    setRejectModal({ isOpen: false, timesheet: null });
                    setRejectReason('');
                },
                onError: (err) => toast.error(err?.response?.data?.detail || `Error: ${err.message}`)
            });
            return;
        }
        rejectTimesheet.mutate({ id: row.id, reason: rejectReason, orgId }, {
            onSuccess: () => {
                toast.success('Timesheet rejected successfully');
                setRejectModal({ isOpen: false, timesheet: null });
                setRejectReason('');
            },
            onError: (err) => toast.error(`Error: ${err.message}`)
        });
    };

    const isApproving = managerApprove.isPending || hrApprove.isPending || laborApprove.isPending || laborHrApprove.isPending;
    const isRejecting = rejectTimesheet.isPending || laborReject.isPending;

    const columns = [
        {
            name: 'Employee',
            selector: row => row.employeeName,
            sortable: true,
            cell: row => {
                const initials = row.employeeName
                    ? row.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                    : '?';
                const bg = AVATAR_COLORS[(row.employeeName?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
                return (
                    <div className='flex items-center gap-10'>
                        <div style={{ backgroundColor: bg }} className='w-[35px] h-[35px] rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0'>
                            {initials}
                        </div>
                        <div>
                            <p className='font-medium text-sm'>{row.employeeName}</p>
                            <p className='text-xs text-text-secondary'>{row.employeeEmail}</p>
                        </div>
                    </div>
                );
            },
            width: "250px"
        },
        {
            name: 'Type',
            selector: row => row.kind,
            sortable: true,
            width: "100px",
            center: true,
            cell: row => (
                <span className={`px-6 py-2 rounded-full text-[10px] font-semibold text-white ${row.kind === 'labor' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                    {row.kind === 'labor' ? 'Labour' : 'Standard'}
                </span>
            ),
        },
        {
            name: 'Period',
            selector: row => `${getMonthName(row.month)} ${row.year}`,
            sortable: true,
            width: "150px"
        },
        {
            name: 'Total Hours',
            selector: row => row.total_hours || 0,
            sortable: true,
            width: "120px",
            center: true,
            cell: row => <span className="font-semibold">{row.total_hours || 0}h</span>
        },
        {
            name: isHR ? 'Mgr Approved' : 'Submitted',
            selector: row => isHR ? formatDate(row.manager_approved_at) : formatDate(row.submitted_at || row.signed_at),
            sortable: true,
            width: "150px"
        },
        {
            name: 'Status',
            selector: row => displayStatus(row),
            format: row => getStatusBadge(displayStatus(row)),
            sortable: true,
            width: "150px"
        },
        {
            name: 'Actions',
            cell: row => {
                const isSelf = String(row.employee_id) === String(user?.employeeId);
                if (isSelf) return <span className='text-[10px] text-gray-400 italic'>Own timesheet</span>;

                // Labour rows in the HR queue can include ones the line manager hasn't
                // approved yet (kept visible so HR can reject if needed) — hr-approve/
                // would 400 on those, so only offer Approve when the status actually
                // allows it for this role.
                const canApproveRow = row.kind === 'labor'
                    ? (isHR ? row.status === 'approved' : row.status === 'submitted')
                    : true;

                const menuItems = [
                    {
                        label: 'View Timesheet',
                        icon: IconEye,
                        onClick: () => navigate(row.kind === 'labor' ? `/approvals/timesheet/labor/${row.id}` : `/approvals/timesheet/${row.id}`),
                        description: "Examine detailed hours and project codes"
                    },
                ];
                if (canApproveRow) {
                    menuItems.push({
                        label: isHR ? 'HR Approve' : 'Approve Details',
                        icon: IconCheck,
                        onClick: () => setApproveModal({ isOpen: true, timesheet: row }),
                        description: isHR ? "Execute final HR approval" : "Verify and approve as line manager"
                    });
                }
                menuItems.push({
                    label: 'Reject Timesheet',
                    icon: IconBan,
                    danger: true,
                    onClick: () => { setRejectModal({ isOpen: true, timesheet: row }); setRejectReason(''); },
                    description: "Send back for corrections"
                });

                return (
                    <div className="flex justify-center">
                        <ActionDropdown menuItems={menuItems} />
                    </div>
                );
            },
            width: "180px",
            center: true
        }
    ];

    const formattedTimesheets = useMemo(() => {
        return timesheets.map(ts => {
            let employeeObj = null;
            if (employees && employees.length > 0) {
                const empId = typeof ts.employee === 'object' ? ts.employee.id : ts.employee;
                employeeObj = employees.find(e => e.id === empId);
            }
            const employeeName = employeeObj
                ? (employeeObj.name || `${employeeObj.first_name} ${employeeObj.last_name}`)
                : (ts.employee_name || 'Unknown');
            const employeeEmail = employeeObj?.email || employeeObj?.work_email || '';
            return { ...ts, employeeName, employeeEmail };
        });
    }, [timesheets, employees]);

    const filteredItems = formattedTimesheets.filter(item => {
        if (!filterText) return true;
        const search = filterText.toLowerCase();
        const getStr = (val) => String(val || '').toLowerCase();
        return (
            getStr(item.employeeName).includes(search) ||
            getStr(item.status).includes(search) ||
            getStr(getMonthName(item.month)).includes(search)
        );
    });

    const subHeaderComponent = useMemo(() => (
        <div className='flex gap-5 mb-10 w-full justify-end'>
            <input
                type='text'
                placeholder='Search...'
                className='form-control w-full max-w-[250px]'
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
            />
        </div>
    ), [filterText]);

    if (isLoading) return <div className='p-20 text-center'>Loading...</div>;

    const ts = approveModal.timesheet;
    const rts = rejectModal.timesheet;
    const approverName = user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username || 'You';

    return (
        <div className='p-20 bg-bg-light min-h-screen'>
            <Breadcrumb title={isHR ? "HR Timesheet Approvals" : "Timesheet Approvals"} path="Approvals / Timesheets" />

            {/* Summary Card */}
            <div className='grid grid-cols-1 md:grid-cols-3 gap-20 mb-30'>
                <div className='bg-white p-20 rounded shadow-sm border border-border'>
                    <p className='text-xs text-text-secondary uppercase font-bold mb-5'>{isHR ? 'Pending HR Review' : 'Pending Approval'}</p>
                    <h3 className='text-3xl font-bold text-warning'>{filteredItems.length}</h3>
                    <p className='text-sm text-text-secondary mt-5'>{isHR ? 'Timesheets awaiting HR approval' : 'Timesheets awaiting your review'}</p>
                </div>
            </div>

            {/* Main Table */}
            <div className='bg-white rounded shadow-sm p-20'>
                <div className='flex justify-between items-center mb-20'>
                    <h2 className='text-lg font-bold'>{isHR ? 'Timesheets Pending HR Approval' : 'Timesheets Pending Approval'}</h2>
                </div>

                {filteredItems.length === 0 ? (
                    <div className='text-center py-40 text-text-secondary'>
                        <p className='text-lg'>No pending timesheets</p>
                        <p className='text-sm mt-5'>You don't have any timesheets awaiting approval.</p>
                    </div>
                ) : (
                    <ReactDataTable
                        columns={columns}
                        data={filteredItems}
                        pagination
                        paginationResetDefaultPage={resetPaginationToggle}
                        subHeader
                        subHeaderComponent={subHeaderComponent}
                        highlightOnHover
                        persistTableHead
                    />
                )}
            </div>

            {/* Approve Modal */}
            {approveModal.isOpen && ts && (
                <>
                    <div className='fixed inset-0 z-[100] flex items-center justify-center p-20'>
                        <div className='w-full max-w-[450px] bg-white rounded-xl shadow-2xl overflow-hidden'>
                            {/* Header */}
                            <div className='bg-primary px-20 py-15 flex items-center justify-between'>
                                <h3 className='text-lg font-semibold text-white flex items-center gap-8'>
                                    <IconCircleCheck size={20} />
                                    {isHR ? 'HR Approval' : 'Approve Timesheet'}
                                </h3>
                                <button
                                    onClick={() => setApproveModal({ isOpen: false, timesheet: null })}
                                    disabled={isApproving}
                                    className='p-4 hover:bg-white/20 rounded-full disabled:opacity-50'
                                >
                                    <IconX size={20} className='text-white' />
                                </button>
                            </div>

                            {/* Body */}
                            <div className='p-20'>
                                {/* Timesheet info */}
                                <div className='bg-gray-50 border border-border rounded-lg p-15 mb-20'>
                                    <div className='flex items-center gap-12 mb-10'>
                                        <div
                                            style={{ backgroundColor: AVATAR_COLORS[(ts.employeeName?.charCodeAt(0) || 0) % AVATAR_COLORS.length] }}
                                            className='w-[40px] h-[40px] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0'
                                        >
                                            {ts.employeeName?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                                        </div>
                                        <div>
                                            <p className='font-semibold text-gray-800'>{ts.employeeName}</p>
                                            <p className='text-xs text-text-secondary'>{ts.employeeEmail}</p>
                                        </div>
                                    </div>
                                    <div className='flex gap-20 text-sm text-gray-600'>
                                        <span><span className='font-medium'>Period:</span> {getMonthName(ts.month)} {ts.year}</span>
                                        <span><span className='font-medium'>Hours:</span> {ts.total_hours || 0}h</span>
                                    </div>
                                </div>

                                {/* Checklist */}
                                <p className='text-sm text-gray-600 mb-10'>By approving, you confirm that:</p>
                                <ul className='text-xs text-gray-500 space-y-6 mb-20'>
                                    <li className='flex items-start gap-8'>
                                        <IconCheck size={14} className='text-primary mt-1 flex-shrink-0' />
                                        The reported hours have been reviewed and are accurate
                                    </li>
                                    <li className='flex items-start gap-8'>
                                        <IconCheck size={14} className='text-primary mt-1 flex-shrink-0' />
                                        Time has been correctly charged to the relevant projects
                                    </li>
                                    {isHR && (
                                        <li className='flex items-start gap-8'>
                                            <IconCheck size={14} className='text-primary mt-1 flex-shrink-0' />
                                            Line manager confirmation has been received
                                        </li>
                                    )}
                                </ul>

                                {/* Approver identity */}
                                <div className='flex items-center gap-15 p-15 bg-primary/10 rounded-lg'>
                                    <div className='w-[40px] h-[40px] rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0'>
                                        <IconCircleCheck size={22} className='text-primary' />
                                    </div>
                                    <div>
                                        <p className='font-semibold text-gray-800'>{approverName}</p>
                                        <p className='text-xs text-gray-500'>{new Date().toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className='px-20 py-15 border-t border-border flex justify-end gap-10'>
                                <button
                                    onClick={() => setApproveModal({ isOpen: false, timesheet: null })}
                                    disabled={isApproving}
                                    className='btn btn-outline-secondary disabled:opacity-50'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmApprove}
                                    disabled={isApproving}
                                    className='btn btn-primary disabled:opacity-60 disabled:cursor-not-allowed'
                                >
                                    {isApproving ? 'Approving...' : 'Confirm Approval'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div onClick={() => !isApproving && setApproveModal({ isOpen: false, timesheet: null })} className='fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm' />
                </>
            )}

            {/* Reject Modal */}
            {rejectModal.isOpen && rts && (
                <>
                    <div className='fixed inset-0 z-[100] flex items-center justify-center p-20'>
                        <div className='w-full max-w-[450px] bg-white rounded-xl shadow-2xl overflow-hidden'>
                            {/* Header */}
                            <div className='bg-danger px-20 py-15 flex items-center justify-between'>
                                <h3 className='text-lg font-semibold text-white flex items-center gap-8'>
                                    <IconAlertTriangle size={20} />
                                    Reject Timesheet
                                </h3>
                                <button
                                    onClick={() => setRejectModal({ isOpen: false, timesheet: null })}
                                    disabled={isRejecting}
                                    className='p-4 hover:bg-white/20 rounded-full disabled:opacity-50'
                                >
                                    <IconX size={20} className='text-white' />
                                </button>
                            </div>

                            {/* Body */}
                            <div className='p-20'>
                                {/* Timesheet info */}
                                <div className='bg-gray-50 border border-border rounded-lg p-15 mb-20'>
                                    <div className='flex items-center gap-12 mb-10'>
                                        <div
                                            style={{ backgroundColor: AVATAR_COLORS[(rts.employeeName?.charCodeAt(0) || 0) % AVATAR_COLORS.length] }}
                                            className='w-[40px] h-[40px] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0'
                                        >
                                            {rts.employeeName?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                                        </div>
                                        <div>
                                            <p className='font-semibold text-gray-800'>{rts.employeeName}</p>
                                            <p className='text-xs text-text-secondary'>{rts.employeeEmail}</p>
                                        </div>
                                    </div>
                                    <div className='flex gap-20 text-sm text-gray-600'>
                                        <span><span className='font-medium'>Period:</span> {getMonthName(rts.month)} {rts.year}</span>
                                        <span><span className='font-medium'>Hours:</span> {rts.total_hours || 0}h</span>
                                    </div>
                                </div>

                                <label className='text-sm font-medium text-gray-700 mb-6 block'>
                                    Reason for rejection <span className='text-danger'>*</span>
                                </label>
                                <textarea
                                    className='w-full border border-border rounded-lg p-10 text-sm focus:border-danger outline-none resize-none'
                                    rows={3}
                                    placeholder="Provide a clear reason so the employee can make corrections..."
                                    value={rejectReason}
                                    onChange={e => setRejectReason(e.target.value)}
                                    disabled={isRejecting}
                                />
                            </div>

                            {/* Footer */}
                            <div className='px-20 py-15 border-t border-border flex justify-end gap-10'>
                                <button
                                    onClick={() => setRejectModal({ isOpen: false, timesheet: null })}
                                    disabled={isRejecting}
                                    className='btn btn-outline-secondary disabled:opacity-50'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmReject}
                                    disabled={isRejecting}
                                    className='btn btn-danger disabled:opacity-60 disabled:cursor-not-allowed'
                                >
                                    {isRejecting ? 'Rejecting...' : 'Reject Timesheet'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div onClick={() => !isRejecting && setRejectModal({ isOpen: false, timesheet: null })} className='fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm' />
                </>
            )}

        </div>
    )
}
